import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Bell,
  BellOff,
  Volume2,
  VolumeX,
  Lock,
  Smartphone,
  Mail,
  MessageSquare,
  ShieldCheck,
  LogOut,
  ChevronRight,
  Apple,
  Zap,
} from "lucide-react";

import AppTopbar from "@/components/AppTopbar";
import BottomNav from "@/components/BottomNav";
import { useAuth } from "@/context/AuthContext";
import { updateMe } from "@/lib/admin";
import {
  isPushSupported,
  getPermission,
  subscribePush,
  unsubscribePush,
} from "@/lib/push";
import { useInstallPrompt } from "@/lib/install";
import { isSoundEnabled, setSoundEnabled } from "@/lib/sound";
import { formatApiError } from "@/lib/api";

const ROLE_LABEL = { admin: "Administrateur", agent: "Agent" };

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, reload } = useAuth();
  const [pushPerm, setPushPerm] = useState(getPermission());
  const [hasSub, setHasSub] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [smsEcoBusy, setSmsEcoBusy] = useState(false);
  const [soundOn, setSoundOnState] = useState(isSoundEnabled());
  const install = useInstallPrompt();

  // Detect existing subscription
  useEffect(() => {
    if (!isPushSupported()) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setHasSub(Boolean(sub));
      } catch (_e) {
        setHasSub(false);
      }
    })();
  }, []);

  if (!user) return null;
  const initials = (user.full_name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  const onToggleEmailSms = async () => {
    setEmailBusy(true);
    try {
      const next = !user.notifications_enabled;
      await updateMe({ notifications_enabled: next });
      await reload();
      toast.success(
        next ? "Notifications email + SMS activées." : "Notifications email + SMS désactivées."
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailBusy(false);
    }
  };

  const onToggleSmsEconomy = async () => {
    setSmsEcoBusy(true);
    try {
      const next = !user.sms_economy;
      await updateMe({ sms_economy: next });
      await reload();
      toast.success(
        next
          ? "Économie SMS activée. Les feux orange enverront seulement le push si vous le recevez."
          : "Économie SMS désactivée. Vous recevrez de nouveau push + SMS pour les feux orange."
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSmsEcoBusy(false);
    }
  };

  const onTogglePush = async () => {
    if (!isPushSupported()) {
      toast.error("Notifications push non supportées par ce navigateur.");
      return;
    }
    if (pushPerm === "denied") {
      toast.error("Les notifications sont bloquées. Autorisez-les dans les réglages du navigateur.");
      return;
    }
    setPushBusy(true);
    try {
      if (hasSub) {
        await unsubscribePush();
        setHasSub(false);
        toast.success("Notifications push désactivées.");
      } else {
        await subscribePush();
        setHasSub(true);
        setPushPerm(getPermission());
        toast.success("Notifications push activées.");
      }
    } catch (err) {
      toast.error(err?.message || "Une erreur est survenue.");
    } finally {
      setPushBusy(false);
    }
  };

  const onToggleSound = () => {
    const next = !soundOn;
    setSoundEnabled(next);
    setSoundOnState(next);
    toast.success(next ? "Son d'urgence activé." : "Son d'urgence désactivé.");
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const pushLabel = !isPushSupported()
    ? "Non supporté par ce navigateur"
    : pushPerm === "denied"
    ? "Bloquées par le navigateur"
    : hasSub
    ? "Activées"
    : pushPerm === "granted"
    ? "Autorisées (cliquez pour activer)"
    : "Désactivées";

  return (
    <div className="min-h-screen bg-[var(--sa-subtle)] pb-24" data-testid="profile-screen">
      <AppTopbar subtitle="Mon profil" />
      <main className="mx-auto max-w-xl px-4 pt-5">
        {/* Account card */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="sa-card overflow-hidden"
          data-testid="profile-account-card"
        >
          <div
            className="px-5 py-5 text-white"
            style={{ background: "linear-gradient(135deg, #2563EB, #1D4ED8)" }}
          >
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/15 text-[22px] font-bold backdrop-blur-md">
                {initials || "?"}
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <h1 className="text-[20px] font-bold tracking-tight">
                  {user.full_name}
                </h1>
                <p className="mt-0.5 truncate text-[13px] text-white/85">
                  {user.email}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex h-5 items-center rounded-full bg-white/25 px-2 text-[10.5px] font-bold uppercase tracking-wider">
                    {ROLE_LABEL[user.role] || user.role}
                  </span>
                  {user.phone ? (
                    <span className="text-[12px] font-mono text-white/80">{user.phone}</span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Notifications */}
        <Section title="Notifications" testId="profile-notifications">
          <ToggleRow
            icon={user.notifications_enabled ? Mail : BellOff}
            title="Notifications email + SMS"
            subtitle={
              user.notifications_enabled
                ? "Vous recevez les alertes équipe par email et SMS."
                : "Vos canaux email + SMS sont désactivés."
            }
            on={user.notifications_enabled}
            onChange={onToggleEmailSms}
            busy={emailBusy}
            testId="toggle-email-sms"
          />
          <Divider />
          <ToggleRow
            icon={Zap}
            title="Économie SMS (push prioritaire)"
            subtitle={
              !user.notifications_enabled
                ? "Activez d'abord les notifications email + SMS."
                : !(hasSub && pushPerm === "granted")
                ? "Activez d'abord les notifications push."
                : user.sms_economy
                ? "Activé : feux orange = push uniquement quand le push vous parvient. Urgences rouges → toujours tous les canaux."
                : "Activez pour ne recevoir que le push (et pas le SMS) sur les feux orange. Les rouges restent tous canaux."
            }
            on={user.sms_economy}
            onChange={onToggleSmsEconomy}
            busy={smsEcoBusy}
            disabled={
              !user.notifications_enabled || !(hasSub && pushPerm === "granted")
            }
            testId="toggle-sms-economy"
          />
          <Divider />
          <ToggleRow
            icon={hasSub && pushPerm === "granted" ? Bell : BellOff}
            title="Notifications push web"
            subtitle={pushLabel}
            on={hasSub && pushPerm === "granted"}
            onChange={onTogglePush}
            busy={pushBusy}
            disabled={!isPushSupported() || pushPerm === "denied"}
            testId="toggle-push"
          />
          <Divider />
          <ToggleRow
            icon={soundOn ? Volume2 : VolumeX}
            title="Son d'urgence sur le tableau de bord"
            subtitle={
              soundOn
                ? "Un bip retentit à l'arrivée d'un feu rouge."
                : "Aucun bip ne sera émis (silencieux)."
            }
            on={soundOn}
            onChange={onToggleSound}
            testId="toggle-sound"
          />
          <Divider />
          <ToggleRow
            icon={MessageSquare}
            title="Téléphone (rappels SMS)"
            subtitle={user.phone || "Aucun numéro renseigné"}
            on={Boolean(user.phone)}
            disabled
            testId="phone-row"
          />
        </Section>

        {/* Confidentiality */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          className="mt-4 rounded-[16px] border border-[#bfdbfe] bg-[var(--sa-blue-soft)] px-5 py-4"
          data-testid="privacy-block"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-[var(--sa-blue-dark)]">
              <Lock size={16} />
            </div>
            <div>
              <h2 className="text-[14px] font-bold text-[var(--sa-blue-dark)]">
                Vos données de localisation
              </h2>
              <p className="mt-1 text-[13px] leading-snug text-[var(--sa-blue-dark)]/85">
                Votre position GPS n'est partagée avec l'équipe{" "}
                <strong>que lorsqu'un feu (vert, orange ou rouge) est actif</strong>. En dehors des
                rendez-vous, votre position n'est jamais collectée ni transmise.
              </p>
            </div>
          </div>
        </motion.section>

        {/* App install */}
        <Section title="Application" testId="profile-app">
          {install.installed ? (
            <Row
              icon={ShieldCheck}
              title="Application installée"
              subtitle="SignalAgent est ajouté à votre écran d'accueil."
              testId="install-installed"
              tone="success"
            />
          ) : install.canPrompt ? (
            <button
              type="button"
              onClick={install.trigger}
              data-testid="install-button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-slate-50"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--sa-blue-soft)] text-[var(--sa-blue-dark)]">
                  <Smartphone size={16} />
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-slate-900">
                    Installer l'app sur l'écran d'accueil
                  </p>
                  <p className="text-[12px] text-slate-500">
                    Lancement plus rapide, notifications fiables
                  </p>
                </div>
              </div>
              <ChevronRight size={18} className="text-slate-400" />
            </button>
          ) : install.ios ? (
            <div className="px-4 py-3" data-testid="install-ios-hint">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-700">
                  <Apple size={16} />
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-slate-900">
                    Installer sur iPhone / iPad
                  </p>
                  <p className="mt-0.5 text-[12.5px] leading-snug text-slate-600">
                    Appuyez sur l'icône <strong>Partager</strong> en bas de Safari, puis choisissez{" "}
                    <strong>« Sur l'écran d'accueil »</strong>.
                  </p>
                  {install.iosVersion && install.iosVersion.major < 16 ? (
                    <p className="mt-1 text-[11.5px] text-amber-700">
                      Notifications push disponibles à partir d'iOS 16.4 — pensez à mettre à jour.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <Row
              icon={Smartphone}
              title="Installation indisponible"
              subtitle="Votre navigateur ne propose pas l'installation. Utilisez Chrome (Android) ou Safari (iOS)."
              testId="install-unavailable"
            />
          )}
        </Section>

        {/* Account actions */}
        <Section title="Compte" testId="profile-account-actions">
          {user.role === "admin" ? (
            <>
              <button
                type="button"
                onClick={() => navigate("/admin")}
                data-testid="profile-go-admin"
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-slate-50"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--sa-blue-soft)] text-[var(--sa-blue-dark)]">
                    <ShieldCheck size={16} />
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-slate-900">
                      Accéder à l'administration
                    </p>
                    <p className="text-[12px] text-slate-500">
                      Gérer les utilisateurs SignalAgent
                    </p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-400" />
              </button>
              <Divider />
            </>
          ) : null}
          <button
            type="button"
            onClick={onLogout}
            data-testid="profile-logout"
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-[14px] font-semibold text-red-600 transition hover:bg-red-50"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-600">
              <LogOut size={16} />
            </div>
            Se déconnecter
          </button>
        </Section>

        <p className="mt-8 pb-2 text-center text-[11px] font-medium text-slate-400">
          Propulsé par C l'Agence
        </p>
      </main>
      <BottomNav />
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.06, ease: [0.16, 1, 0.3, 1] }}
      className="mt-4"
      data-testid={testId}
    >
      <h2 className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">
        {title}
      </h2>
      <div className="sa-card overflow-hidden">{children}</div>
    </motion.section>
  );
}

function Row({ icon: Icon, title, subtitle, testId, tone = "default" }) {
  const bg =
    tone === "success"
      ? "bg-emerald-50 text-emerald-600"
      : "bg-slate-100 text-slate-600";
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={testId}>
      <div className={`flex h-9 w-9 items-center justify-center rounded-full ${bg}`}>
        <Icon size={16} />
      </div>
      <div className="flex flex-col">
        <p className="text-[14px] font-semibold text-slate-900">{title}</p>
        <p className="text-[12.5px] text-slate-500">{subtitle}</p>
      </div>
    </div>
  );
}

function ToggleRow({ icon: Icon, title, subtitle, on, onChange, busy, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      disabled={disabled || busy}
      data-testid={testId}
      className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition ${
        disabled ? "opacity-60 cursor-not-allowed" : "hover:bg-slate-50"
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            on ? "bg-[var(--sa-blue-soft)] text-[var(--sa-blue-dark)]" : "bg-slate-100 text-slate-500"
          }`}
        >
          <Icon size={16} />
        </div>
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-slate-900">{title}</p>
          <p className="truncate text-[12.5px] text-slate-500">{subtitle}</p>
        </div>
      </div>
      <span
        aria-hidden
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          on ? "bg-[var(--sa-blue)]" : "bg-slate-200"
        } ${disabled ? "opacity-60" : ""}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
            on ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}

function Divider() {
  return <div className="border-t border-slate-100" />;
}
